import { AxiosInstance } from "axios";
import { CompanyItem, failure, ok, pending, Result } from "@/common/common";
import { makeAutoObservable } from "mobx";
import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import AlertMessageStore from "@/stores/AlertMessageStore";
import {
  extractPortfolioContactsUrl,
  SelectedPortfolioOption,
  toFollowUpCounts,
} from "@/portfolio/portfolioDomain";
import UserStore from "@/stores/UserStore";
import { AccessType } from "@/types/enums/AccessType";
import { RootStore } from "@/stores/RootStore";
import { compact } from "lodash";

export class FollowUpShareholdersStore {
  fetchResponse?: Result<GetShareholdersFollowUpResponse>;
  showReviewDialogData?: {
    data: ShareholderUpdatedNameFollowUp;
    model: OptionFieldModel<ShareholderUpdatedNameFollowUpItem>;
  };
  isSubmitting = false;
  nameFilter = new StringFieldModel();
  companyFilter: RadioFieldModel<{ orgNo: string; name: string } | null>;

  constructor(
    readonly http: AxiosInstance,
    readonly messageStore: AlertMessageStore,
    readonly userStore: UserStore,
    readonly rootStore: RootStore,
    readonly portfolio: SelectedPortfolioOption,
    readonly contextCompany?: string,
  ) {
    this.companyFilter = new RadioFieldModel<{ orgNo: string; name: string } | null>({
      value: null,
      options: [],
      idFn: (value) => (value ? value.orgNo : "all"),
      labelFn: (value) => (value ? { translated: value.name } : { key: "portfolio.all-companies" }),
    });

    makeAutoObservable(
      this,
      { http: false, messageStore: false, userStore: false },
      { autoBind: true },
    );
  }

  *fetchFollowUps() {
    this.fetchResponse = pending();
    try {
      const url = toFollowUpShareholdersApiUrl(this.portfolio, this.contextCompany);
      if (!url) return;

      const response: GetShareholdersFollowUpResponse = yield this.http
        .get<GetShareholdersFollowUpResponse>(url)
        .then((res) => res.data);
      this.fetchResponse = ok(response);
      this.userStore.dispatchEvent([
        { type: "update-store", data: { followUpCounts: toFollowUpCounts(response) } },
      ]);
      const options = compact(
        response.followUp.map((d) =>
          d.type !== "missing-email" && !d.isDismissed
            ? {
                orgNo: d.orgNo,
                name: d.companyName,
              }
            : undefined,
        ),
      );
      this.companyFilter.updateOptions([null, ...options]);
      if (!this.companyFilter.options.some(this.companyFilter.isSelected)) {
        this.companyFilter.setValue(null);
      }
    } catch (e) {
      this.fetchResponse = failure();
      this.messageStore.showGeneralError();
    }
  }

  openReviewDialog(data: ShareholderUpdatedNameFollowUp) {
    this.showReviewDialogData = {
      data,
      model: new OptionFieldModel({
        selectedIds: data.shareholders.map((s) => s.companyNumber),
        options: data.shareholders,
        idFn: (value) => value.companyNumber,
        labelFn: (value) => ({ translated: value.companyName }),
      }),
    };
  }

  closeReviewDialog() {
    this.showReviewDialogData = undefined;
  }

  get navigateToPortfolioContacts(): () => void {
    return () => {
      this.userStore.dispatchEvent([
        { type: "navigate", url: extractPortfolioContactsUrl(this.portfolio) },
      ]);
      this.rootStore.portfolioStore.missingEmailModel.setValue(false);
      this.rootStore.portfolioStore.companyFilter?.setValue(null);
    };
  }

  get reviewContactsByMissingEmail() {
    const companyItem: CompanyItem | null = this.contextCompany
      ? {
          organisationNumber: this.contextCompany,
          companyName: this.userStore.companyInfo?.company.name ?? "",
        }
      : null;
    return () => {
      this.userStore.dispatchEvent([
        {
          type: "navigate",
          url: extractPortfolioContactsUrl(this.portfolio),
          state: {
            missingEmail: true,
            company: companyItem?.organisationNumber,
          },
        },
      ]);
    };
  }

  *submitReviewDialog() {
    this.isSubmitting = true;
    try {
      const shareholderOrgNos = this.showReviewDialogData?.model.selectedIds;
      yield this.http.post("/portfolio/follow-up/shareholders/update", {
        orgNo: this.showReviewDialogData?.data.orgNo,
        shareholderOrgNos: shareholderOrgNos,
      });
      this.showReviewDialogData = undefined;
      this.messageStore.showSuccess({
        key: "followup.shareholders.dialog-success",
        params: {
          count: shareholderOrgNos?.size,
        },
      });

      this.fetchFollowUps();
    } catch (e) {
      this.messageStore.showGeneralError();
    } finally {
      this.isSubmitting = false;
    }
  }

  *dismissUpdates(orgNo: string) {
    try {
      yield this.http.delete(`/portfolio/follow-up/shareholders/${orgNo}`);
      this.fetchFollowUps();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  filterData(data: ShareholderFollowUp[]) {
    const companyFilter = this.companyFilter.value;
    const filteredData = companyFilter
      ? data.filter((d) => d.type !== "missing-email" && d.orgNo === companyFilter.orgNo)
      : data;
    const nameFilter = this.nameFilter.inputValue.toLowerCase().trim();
    return nameFilter
      ? filteredData.filter(
          (x) => x.type !== "missing-email" && x.companyName.toLowerCase().includes(nameFilter),
        )
      : filteredData;
  }
}

export type GetShareholdersFollowUpResponse = {
  followUp: ShareholderFollowUp[];
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
};

export type ShareholderFollowUp =
  | ShareholderUpdatedNameFollowUp
  | ShareholderMissingNaturalIdFollowUp
  | ShareholderMissingEmailFollowUp;

export type ShareholderUpdatedNameFollowUp = {
  type: "updated-name";
  orgNo: string;
  companyName: string;
  shareholders: ShareholderUpdatedNameFollowUpItem[];
  isDismissed: boolean;
};

export type ShareholderUpdatedNameFollowUpItem = {
  companyName: string;
  companyNumber: string;
  brName: string;
};

export type ShareholderMissingNaturalIdFollowUp = {
  type: "missing-naturalid";
  orgNo: string;
  companyName: string;
  shareholders: ShareholderMissingNaturalIdFollowUpItem[];
  isDismissed: boolean;
};

export type ShareholderMissingEmailFollowUp = {
  type: "missing-email";
};

export type ShareholderMissingNaturalIdFollowUpItem = {
  shareholderId: string;
  isCompany: boolean;
  name: string;
};

const toFollowUpShareholdersApiUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  contextCompany?: string,
): string | null => {
  const format = (part: string, context: string = "") =>
    "/portfolio" + part.toLowerCase() + "/follow-up/shareholders" + context;
  switch (selectedPortfolioOption.type) {
    case "all-companies":
      return format("", contextCompany ? `/${contextCompany}` : "");
    case "with-admin-role":
      return format(`/role/${AccessType.ADMIN}`);
    case "with-board-role":
      return null;
    case "my-investments":
      return null;
    case "portfolio-instance":
      return format(`/${selectedPortfolioOption.portfolio.id}`);
  }
};

export const followUpShareholderTabs = ["followup", "dismissed"] as const;

export type ShareholderFollowUpTab = (typeof followUpShareholderTabs)[number];

export const followUpShareholderTabToLabel = {
  followup: "follow-up.shareholders.tab-follow-up",
  dismissed: "follow-up.shareholders.tab-dismissed",
} as const;

export function mapToFollowUpShareholderTab(tab?: string): ShareholderFollowUpTab {
  return tab === "dismissed" ? "dismissed" : "followup";
}

export function filterShareholderFollowUps(
  followUps: ShareholderFollowUp[],
  tab: ShareholderFollowUpTab,
) {
  return tab === "followup"
    ? followUps.filter((f) => f.type === "missing-email" || !f.isDismissed)
    : followUps.filter((f) => f.type !== "missing-email" && f.isDismissed);
}
